import { useEffect, useState } from "react";
import {
  AlertCircle,
  ArrowUpDown,
  Check,
  Eye,
  FileText,
  MoreHorizontal,
  Search,
  Trash,
  Undo,
  Download,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import Table from "../common/table/Table";
import {
  deletedPageRestore,
  getDeletedPages,
} from "../../API/Company-admin/userApi";
import { getAllPages } from "../../Redux/Actions/pageActions";

export default function PagesManagement() {
  const { userInfo } = useSelector((state) => state.userLogin);
  let dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState("");
  const [deletedPagesList, setDeletedPagesList] = useState([]);
  const [activeTab, setActiveTab] = useState("active");
  const [showAlert, setShowAlert] = useState({
    show: false,
    type: "",
    message: "",
  });
  const [showDeleteDialog, setShowDeleteDialog] = useState({
    show: false,
    pageName: "",
  });

  const [currentPage, setCurrentPage] = useState(1);

  const { pageList, loading, error, emptyPage,rowCount } = useSelector(
    (state) => state.allPagesList
  );

  const filteredData = pageList?.filter((page) =>
    page.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const filteredPageRestoreData = deletedPagesList?.filter((page) =>
    page.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const pageSize = 10;

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const getDeletedPagesData = async () => {
    try {
      const data = await getDeletedPages(userInfo?.token);
      setDeletedPagesList(data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getDeletedPagesData();
    dispatch(getAllPages(currentPage,pageSize));
  }, [currentPage, pageSize]);

  const handleAction = async (action, pageName) => {
    let message = "";
    if (action === "delete") {
      message = `Page "${pageName}" has been deleted.`;
    } else if (action === "restore") {
      message = `Page "${pageName}" has been restored.`;
    }

    try {
      const data = await deletedPageRestore(pageName, userInfo?.token);
      getDeletedPagesData();
    } catch (error) {
      console.log(error);
    }

    setShowAlert({
      show: true,
      type: action === "delete" ? "error" : "success",
      message,
    });

    setShowDeleteDialog({ show: false, pageName: "" });

    setTimeout(
      () => setShowAlert({ show: false, type: "", message: "" }),
      3000
    );
  };

  const columnsProjectPagesTable = [
    {
      header: "Project ID",
      accessor: "project",
    },
    {
      header: "Project Name",
      accessor: "project_name",
    },
    {
      header: "Home Page",
      accessor: "is_home",
      cell: (value) => (value ? "Yes" : "No"),
    },
    {
      header: "Page Name",
      accessor: "name",
    },
    {
      header: "SEO Title",
      accessor: "seo_title",
    },
    {
      header: "Meta Description",
      accessor: "meta_description",
    },
    {
      header: "Meta Keywords",
      accessor: "meta_keywords",
    },
    {
      header: "Meta Tags",
      accessor: "meta_tags",
    },
    {
      header: "Updated At",
      accessor: "updatedAt",
      cell: (value) =>
        value ? new Date(value).toLocaleDateString("en-GB") : "N/A",
    },
    {
      header: "Author",
      accessor: "author",
    },
    {
      header: "Mobile Page",
      accessor: "is_mobile",
      cell: (value) => (value === null ? "N/A" : value ? "Yes" : "No"),
    },
  ];

  return (
    <div className="space-y-6">
      {showAlert.show && (
        <div
          className={`rounded-lg ${
            showAlert.type === "error"
              ? "bg-red-50 text-red-800"
              : "bg-green-50 text-green-800"
          } p-4`}
        >
          <div className="flex">
            <div className="flex-shrink-0">
              {showAlert.type === "error" ? (
                <Trash className="h-5 w-5" />
              ) : (
                <Check className="h-5 w-5" />
              )}
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium">
                {showAlert.type === "error" ? "Page Deleted" : "Page Restored"}
              </h3>
              <div className="mt-2 text-sm">{showAlert.message}</div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-md overflow-hidden rounded-lg bg-white shadow-xl">
            <div className="px-6 py-4">
              <h3 className="text-lg font-medium text-gray-900">Delete Page</h3>
              <p className="mt-2 text-sm text-gray-500">
                Are you sure you want to delete "{showDeleteDialog.pageName}"?
                This action can be undone later.
              </p>
            </div>
            <div className="flex justify-end bg-gray-50 px-6 py-3">
              <button
                type="button"
                className="mr-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                onClick={() =>
                  setShowDeleteDialog({ show: false, pageName: "" })
                }
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
                onClick={() =>
                  handleAction("delete", showDeleteDialog.pageName)
                }
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="relative w-full max-w-sm">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <Search className="h-4 w-4 text-gray-500" />
          </div>
          <input
            type="text"
            placeholder="Search pages..."
            className="block w-full rounded-md border border-gray-300 bg-white py-2 pl-10 pr-3 shadow-sm focus:border-red-500 focus:outline-none focus:ring-red-500 sm:text-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div>
        <div className="mb-4 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            <button
              style={
                activeTab === "active" ? { borderBottom: "2px solid" } : {}
              }
              onClick={() => setActiveTab("active")}
              className={`cursor-pointer flex items-center whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium ${
                activeTab === "active"
                  ? "border-red-500 text-red-600"
                  : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
              }`}
            >
              <FileText className="mr-2 h-4 w-4" />
              Active Pages
            </button>
            <button
              style={
                activeTab === "deleted" ? { borderBottom: "2px solid" } : {}
              }
              onClick={() => setActiveTab("deleted")}
              className={`cursor-pointer pointer flex items-center whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium ${
                activeTab === "deleted"
                  ? "border-red-500 text-red-600"
                  : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
              }`}
            >
              <Trash className="mr-2 h-4 w-4" />
              Deleted Pages
            </button>
          </nav>
        </div>

        {activeTab === "active" && (
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Active Pages
              </h3>
              <p className="text-sm text-gray-500">Manage your active pages</p>
            </div>
            {/* <div className="px-6 py-4">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                      >
                        Page Name
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                      >
                        <div className="flex items-center gap-1">
                          Last Modified
                          <ArrowUpDown className="h-3 w-3" />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                      >
                        Status
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500"
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {activePages
                      .filter((page) => page.name.toLowerCase().includes(searchTerm.toLowerCase()))
                      .map((page) => (
                        <tr key={page.id}>
                          <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">{page.name}</td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">{page.lastModified}</td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                            <span
                              className={`inline-flex rounded-full ${
                                page.status === "Published"
                                  ? "bg-green-600"
                                  : page.status === "Downloaded"
                                    ? "bg-blue-600"
                                    : "bg-amber-600"
                              } px-2 text-xs font-semibold leading-5 text-white`}
                            >
                              {page.status}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                            <div className="relative inline-block text-left">
                              <button
                                type="button"
                                className="flex items-center rounded-md bg-white text-gray-400 hover:text-gray-600 focus:outline-none"
                                id={`menu-button-${page.id}`}
                                aria-expanded="true"
                                aria-haspopup="true"
                              >
                                <span className="sr-only">Open options</span>
                                <MoreHorizontal className="h-5 w-5" />
                              </button>
                              {/* <div
                                className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                                role="menu"
                                aria-orientation="vertical"
                                aria-labelledby={`menu-button-${page.id}`}
                              >
                                <div className="py-1" role="none">
                                  <button
                                    className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    role="menuitem"
                                  >
                                    <Eye className="mr-3 h-4 w-4" />
                                    View
                                  </button>
                                  <button
                                    className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    role="menuitem"
                                  >
                                    <Download className="mr-3 h-4 w-4" />
                                    Download
                                  </button>
                                  <button
                                    className="flex w-full items-center px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                                    role="menuitem"
                                    onClick={() => setShowDeleteDialog({ show: true, pageName: page.name })}
                                  >
                                    <Trash className="mr-3 h-4 w-4 text-red-600" />
                                    Delete
                                  </button>
                                </div>
                              </div> */}
            {/* </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div> */}
            {/* // </div> */}
            <Table
              data={filteredData}
              columns={columnsProjectPagesTable}
              pagination={{
                currentPage,
                pageSize,
                totalItems:rowCount,
                onPageChange: handlePageChange,
              }}
            />
          </div>
        )}

        {activeTab === "deleted" && (
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Deleted Pages
              </h3>
              <p className="text-sm text-gray-500">
                Restore previously deleted pages
              </p>
            </div>
            <div className="px-6 py-1">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                      >
                        Page Name
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                      >
                        Deleted On
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500"
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {filteredPageRestoreData?.length > 0 ? (
                      filteredPageRestoreData?.map((page) => (
                        <tr key={page._id}>
                          <td className="whitespace-nowrap px-6 py-2 text-sm font-medium text-gray-900">
                            {page.name}
                          </td>
                          <td className="whitespace-nowrap px-6 py-2 text-sm text-gray-500">
                            {new Date(page.createdAt).toLocaleString()}
                          </td>
                          <td className="whitespace-nowrap px-6 py-2 text-right text-sm font-medium">
                            <button
                              className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                              onClick={() => handleAction("restore", page._id)}
                            >
                              <Undo className="mr-2 h-3 w-3" />
                              Restore
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          className="px-6 py-4 text-center text-sm text-gray-500"
                          colSpan={3}
                        >
                          No data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="border-t border-gray-200 px-6 py-4">
              <div className="flex items-center gap-2 rounded-lg bg-amber-50 p-3 text-amber-800">
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <p className="text-sm">
                  Deleted pages are kept for 30 days before being permanently
                  removed.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
